'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Button, Select } from '@/components/ui';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export type PageSizeOption = number;

export interface PaginationNavProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: PageSizeOption[];
  showPageSizeSelector?: boolean;
  /** Translation namespace for labels. Defaults to "DataTable". */
  translationNamespace?: string;
  /**
   * Compact, single-line pager (just ‹ Page X/Y ›) with no "showing N of M"
   * summary or page-size selector — for narrow contexts like a master-detail rail.
   */
  compact?: boolean;
}

const DEFAULT_PAGE_SIZE_OPTIONS: PageSizeOption[] = [10, 25, 50, 100];

export function PaginationNav({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  showPageSizeSelector = true,
  translationNamespace = 'DataTable',
  compact = false,
}: PaginationNavProps) {
  const t = useTranslations(translationNamespace);

  const totalPages = total === 0 ? 1 : Math.max(1, Math.ceil(total / pageSize));
  const startRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow = total === 0 ? 0 : Math.min(page * pageSize, total);

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onPageSizeChange?.(Number(e.target.value));
    onPageChange(1);
  };

  if (total === 0) {
    return null;
  }

  const showPagination = totalPages > 1;

  if (compact) {
    if (!showPagination) return null;
    return (
      <div className="border-border flex items-center justify-between gap-2 border-t pt-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          aria-label={t('pagination.previous')}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
        <span className="text-muted text-xs">{t('pagination.pageOf', { page, totalPages })}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          aria-label={t('pagination.next')}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="border-border border-t pt-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          {showPageSizeSelector && onPageSizeChange && (
            <div className="flex items-center gap-2">
              <label htmlFor="pagination-page-size" className="text-muted text-sm">
                {t('pagination.rowsPerPage')}
              </label>
              <Select
                id="pagination-page-size"
                value={String(pageSize)}
                onChange={handlePageSizeChange}
                className="h-9 w-[100px]"
              >
                {pageSizeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <span className="text-foreground text-sm">
            {t('pagination.showing', { start: startRow, end: endRow, total })}
          </span>
        </div>
        {showPagination && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              aria-label={t('pagination.previous')}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <span className="text-muted text-sm">
              {t('pagination.pageOf', { page, totalPages })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              aria-label={t('pagination.next')}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
